<template>
  <div class="gs-promo-areas">
    <div v-if="!resources.length" class="gs-promo-areas__empty">
      <span class="gs-promo-areas__empty-icon" aria-hidden="true">
        <gs-icon name="plus" size="22" />
      </span>
      <h3>No promo tools for this funnel yet</h3>
      <p>{{ $t('frontend_resources_no_funnel') }}</p>
      <gs-button variant="primary" @click="$emit('openCreateResourceModal')">
        {{ $t('frontend_resources_index_create_new_funnel') }}
      </gs-button>
    </div>

    <div v-else class="gs-promo-areas__grid">
      <promo-area-card
        v-for="row in resources"
        :key="row.id"
        :resource="row"
        :funnel="funnel"
        :share-enabled="!!(funnel && funnel.is_live && funnel.slug)"
        @edit-assets="openResourceAssets(row.id)"
        @share="openShare(row)"
        @copy-signup-link="copySignupLink(row)"
        @settings="openEditPromoAreaModal(row.id)"
        @make-default="openConfirmModal(row.id, 'markAsDefault')"
        @delete="openConfirmModal(row.id, 'deletePromoArea')"
        @toggle-status="openConfirmModal(row.id, 'changePromoToolStatus', $event)"
        @preview="openPreview(row)"
      />
    </div>

    <gs-alert-dialog
      v-model="confirmOpen"
      :title="$t('frontend_sure')"
      :copy="messageContent"
      :confirm-label="buttonText"
      :cancel-label="$t('frontend_cancel')"
      :variant="actionType === 'deletePromoArea' ? 'danger' : 'primary'"
      @confirm="applyAction()"
    />
  </div>
</template>

<script>
import Notify from '@groovepages/gd-ui-kit/notify';
import router from '@/router';
import axios from 'axios';
import FunnelResource from '@/models/FunnelResource';
import PromoAreaCard from './PromoAreaCard.vue';

export default {
  components: {
    PromoAreaCard,
  },
  props: {
    resources: {
      type: Array,
      default: () => [],
    },
    funnel: {
      type: Object,
      default: () => { },
    },
    model: {
      type: Object,
      default: () => { },
    },
  },
  data() {
    return {
      resourceId: null,
      newStatus: '',
      actionType: '',
      messageContent: '',
      buttonText: '',
      confirmOpen: false,
      localModel: this.model,
    }
  },
  methods: {
    openResourceAssets(resourceId) {
      router.push({
        name: 'funnel_resource_assets',
        params: {
          id: this.funnel.id,
          resource_id: resourceId,
        },
      });
    },
    openShare(row) {
      this.$emit('openShare', row.permalink);
    },
    isDefaultResource(resourceId) {
      const resource = this.resources.find((item) => item.id === resourceId);

      return !!(resource && resource.is_default);
    },
    async copySignupLink(row) {
      const affiliatePortalLink = this.getFunnelAffiliatePortal(row.permalink);

      try {
        await this.$copyText(affiliatePortalLink);
        this.onCopyAffiliateLink();
      } catch (error) {
        this.onErrorAffiliateLinkCopy();
      }
    },
    openPreview(row) {
      const href = this.$router.resolve({
        name: 'promo_tools',
        params: { resource_id: row.permalink },
      }).href;

      window.open(`${window.location.origin}${href}`, '_blank');
    },
    openConfirmModal(resourceId, actionType, newStatus = '') {
      // The default promo area is the affiliate fallback; the legacy table
      // disabled its delete action and the card keeps that guard.
      if (actionType === 'deletePromoArea' && this.isDefaultResource(resourceId)) {
        return;
      }

      this.resourceId = resourceId;
      this.newStatus = newStatus;
      this.actionType = actionType;
      switch (actionType) {
        case 'markAsDefault':
          this.messageContent = this.$t('frontend_groovesell_resource_swal_title');
          this.buttonText = this.$t('frontend_groovesell_resource_swal_confirm_text');
          break;
        case 'deletePromoArea':
          this.messageContent = this.$t('frontend_non_revertable');
          this.buttonText = this.$t('frontend_delete_confirm');
          break;
        case 'changePromoToolStatus':
          this.messageContent = `${this.$t('frontend_groovesell_tools_affiliate_want_to')} ${newStatus
            ? this.$t('frontend_groovesell_tools_affiliate_enable')
            : this.$t('frontend_groovesell_tools_affiliate_disable')} ${this.$t('frontend_groovesell_tools_affiliate_this_promo_tool')}`;
          this.buttonText = `${this.$t('frontend_groovesell_tools_affiliate_yes')}, ${this.newStatus
            ? this.$t('frontend_groovesell_tools_affiliate_enable_it')
            : this.$t('frontend_groovesell_tools_affiliate_disable_it')}`;
          break;
      }

      this.confirmOpen = true;
    },
    applyAction() {
      switch (this.actionType) {
        case 'markAsDefault':
          this.markAsDefault();
          break;
        case 'deletePromoArea':
          this.deletePromoArea();
          break;
        case 'changePromoToolStatus':
          this.changePromoToolStatus();
          break;
      }
    },
    async markAsDefault() {
      this.$loader.start('markAsDefault');
      await axios.post('/funnel-resources/make-default', {
        id: this.resourceId,
      });

      const resources = await this.funnel.promotional_areas().$get();

      this.$emit('updateResources', resources);
      this.$loader.end('markAsDefault');
    },
    getFunnelAffiliatePortal(resourcePermalink) {
      if (this.funnel && this.funnel.slug) {
        const siteAppurl = window.config.appUrl.replace(/^https?:\/\//i, '');

        return `https://${this.funnel.slug}.${siteAppurl}/${this.funnel.id}/affiliate-signup/${resourcePermalink}`;
      }

      return '';
    },
    onCopyAffiliateLink() {
      Notify.success('Affiliate Signup Link Copied To Your Clipboard');
    },
    onErrorAffiliateLinkCopy() {
      Notify.fail('Unable To Copy Affiliate Signup Link');
    },
    async openEditPromoAreaModal(resourceId) {
      this.$loader.start('openEditPromoAreaModal');
      const resource = await FunnelResource.$find(resourceId);

      this.localModel.name = resource.name;
      this.localModel.landing_page_id = null;

      if (
        resource.landing_page_id &&
        this.funnel.main_product.affiliate_landing_pages.some(
          (el) => el.id == resource.landing_page_id
        )
      ) {
        this.localModel.landing_page_id = resource.landing_page_id;
      } else if (this.funnel.main_product.affiliate_landing_pages.length) {
        this.localModel.landing_page_id = this.funnel.main_product.affiliate_landing_pages[0].id;
      }

      this.localModel.use_custom_video = resource.use_custom_video;
      this.localModel.video_link = resource.video_link;
      this.localModel.show_more_funnels = resource.show_more_funnels;
      this.localModel.related_funnels = resource.related_funnels;
      this.localModel.show_more_resources = resource.show_more_resources;
      this.localModel.related_resources = resource.related_resources || [];
      this.localModel.landing_pages = resource.landing_pages;
      this.localModel.header_text = resource.header_text;
      this.localModel.login_text = resource.login_text;
      this.localModel.signup_text = resource.signup_text;
      this.localModel.disable_branding = resource.disable_branding;

      this.$emit('updateModel', this.localModel);
      this.$emit('updateResource', resource);
      this.$root.$emit('modal-open', 'edit-promo-area-modal');
      this.$loader.end('openEditPromoAreaModal');
    },
    async deletePromoArea() {
      this.$loader.start('deletePromoArea');

      const resources = this.resources;
      const resource = await FunnelResource.$find(this.resourceId);
      await resource.delete();

      const deleted_resource = resources
        .map((item) => item.id)
        .indexOf(this.resourceId);
      resources.splice(deleted_resource, 1);

      this.$emit('updateResources', resources);
      this.$loader.end('deletePromoArea');
    },
    async changePromoToolStatus() {
      this.$loader.start('changePromoToolStatus');

      await axios.post('/funnel-resources/update-status', {
        id: this.resourceId,
        status: this.newStatus,
      });

      const resources = await this.funnel.promotional_areas().$get();

      this.$emit('updateResources', resources);
      this.$loader.end('changePromoToolStatus');
    },
  }
}
</script>
<style scoped>
.gs-promo-areas {
  margin-top: 20px;
}

.gs-promo-areas__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
  gap: 18px;
  align-items: stretch;
}

.gs-promo-areas__empty {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  padding: 44px 32px 48px;
  text-align: center;
  background: var(--gs-surface);
  border: 1px dashed var(--gs-border-strong);
  border-radius: var(--gs-radius-lg);
}

.gs-promo-areas__empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: 4px;
  color: var(--gs-primary);
  background: var(--gs-primary-soft);
  border-radius: 999px;
}

.gs-promo-areas__empty h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 650;
}

.gs-promo-areas__empty p {
  margin: 0 0 12px;
  max-width: 48ch;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.55;
}

@media (max-width: 1023px) {
  .gs-promo-areas__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
